import Controller from '@ember/controller';
import DS from 'ember-data';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { dropTask } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import { DataOutTypeRouteModel } from 'cio/pods/integrations/data_out/type/route';
import { config } from 'cio/helpers/preview-whitelist';
import { sanitize } from 'cio/utils/sanitize';

const detailsConfig = {
  ...config,
  elements: ['strong', 'b', 'i', 'em', 'a', 'br'],
};

export default class IntegrationDataOutTypeController extends Controller {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;
  @service router!: RouterService;
  @service alerts!: AlertsService;

  declare model: DataOutTypeRouteModel;

  @computed('model.dataModel.hasDirtyAttributes')
  get isDirty() {
    return this.model.dataModel.hasDirtyAttributes;
  }

  @computed('model.integration.detailsPageDescription')
  get details() {
    return sanitize(this.model.integration.detailsPageDescription, detailsConfig);
  }

  saveIntegration = dropTask(async () => {
    let { dataModel, integration } = this.model;
    let isExistingIntegration = Boolean(dataModel.id);
    try {
      let enabled = dataModel.get('isNew')
        ? dataModel.hasEventsSelected
        : dataModel.hasEventsSelected && dataModel.enabled;

      dataModel.set('enabled', enabled);

      await dataModel.save({ adapterOptions: { type: integration.id } });
      integration.set('nativeDataOutEnabled', true);
      this.alerts.success(
        'data-out-type-create.success',
        `${integration.name} has been ${isExistingIntegration ? 'updated' : 'added'}.`,
      );
    } catch (e) {
      this.alerts.error(
        'data-out-type-create.error',
        `Something went wrong when we tried to ${isExistingIntegration ? 'update' : 'create'} ${
          integration.name
        } integration. Please try again.`,
        'Uh oh',
      );
    }
  });

  disconnectIntegrationTask = dropTask(async () => {
    let { dataModel, integration } = this.model;
    try {
      await dataModel.destroyRecord({ adapterOptions: { type: integration.id } });
      integration.set('nativeDataOutEnabled', false);
      this.alerts.success('data-out-type-delete.success', `Removed ${integration.name} integration`);

      this.send('reloadModel');
    } catch {
      dataModel.rollbackAttributes();
      this.alerts.error(
        'data-out-type-delete.error',
        `Could not remove ${integration.name} integration. Please try again.`,
        'Whoops!',
      );
    }
  });
}
